import React, { useState, useEffect } from 'react';
import { FaPalette, FaIdCard, FaUpload, FaSave, FaRedo, FaRulerCombined } from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';

// URL Helper
// Pastikan REACT_APP_BACKEND di .env mengarah ke backend yang benar (https jika production)
const BACKEND_URL = process.env.REACT_APP_BACKEND || '';
const UPLOAD_URL = `${BACKEND_URL}/uploads/settings/`;

// HELPER KONVERSI UNIT
const mmToPt = (mm) => (mm * 2.83465);
const ptToMm = (pt) => Math.round(pt / 2.83465);

const TabIdCard = ({ reloadSettings }) => {
    const [isLoading, setIsLoading] = useState(false);

    // Default Config (Ukuran Default ~90x120mm)
    const defaultConfig = {
        page: { width: 255, height: 340, backgroundColor: '#ffffff', backgroundImage: '' },
        header: { height: 70, backgroundColor: '#1e293b', paddingTop: 10, paddingHorizontal: 12, alignItems: 'flex-start' },
        logo: { show: true, width: 40, height: 40, marginTop: 0, url: '' },
        event_title: { 
            text: "CHAMPIONSHIP SERIES 2026", 
            fontSize: 7, 
            fontWeight: 'bold',
            color: '#fbbf24', 
            textAlign: 'right', 
            marginTop: 5, 
            width: '60%' 
        },
        photo: { 
            width: 100, height: 125, marginTop: -20, 
            borderRadius: 4, borderWidth: 2, borderColor: '#ffffff', backgroundColor: '#f3f4f6' 
        },
        athlete_name: { 
            fontSize: 14, color: '#0f172a', marginTop: 10, marginBottom: 2, fontWeight: 'bold' 
        },
        team_name: { 
            fontSize: 10, color: '#64748b', marginBottom: 8, fontWeight: 'normal' 
        },
        badges: { 
            kyorugiBg: '#ea580c', 
            poomsaeBg: '#7c3aed', 
            textColor: '#ffffff', 
            fontSize: 8
        },
        detail_text: { 
            fontSize: 12, color: '#1e293b', borderColor: '#cbd5e1', marginTop: 5 
        },
        footer: { 
            height: 40, backgroundColor: '#f8fafc', 
            mainTextColor: '#0f172a', subTextColor: '#64748b', 
            borderTopColor: '#e2e8f0' 
        }
    };

    const [idCardConfig, setIdCardConfig] = useState(defaultConfig);

    // File States
    const [logoFile, setLogoFile] = useState(null);
    const [bgFile, setBgFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [bgPreview, setBgPreview] = useState(null);

    // Load Data
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/settings/3');
                if (res.data && res.data.config) {
                    const parsed = typeof res.data.config === 'string' ? JSON.parse(res.data.config) : res.data.config;
                    
                    // Deep merge config
                    setIdCardConfig(prev => ({
                        ...defaultConfig,
                        ...parsed,
                        page: { ...defaultConfig.page, ...parsed.page },
                        header: { ...defaultConfig.header, ...parsed.header },
                        logo: { ...defaultConfig.logo, ...parsed.logo },
                        event_title: { ...defaultConfig.event_title, ...parsed.event_title },
                        photo: { ...defaultConfig.photo, ...parsed.photo },
                        athlete_name: { ...defaultConfig.athlete_name, ...parsed.athlete_name },
                        team_name: { ...defaultConfig.team_name, ...parsed.team_name },
                        badges: { ...defaultConfig.badges, ...parsed.badges },
                        detail_text: { ...defaultConfig.detail_text, ...parsed.detail_text },
                        footer: { ...defaultConfig.footer, ...parsed.footer },
                    }));
                    
                    // SET PREVIEW URL DARI SERVER
                    if (parsed.logo?.url) {
                        // Tambahkan timestamp agar cache browser tidak menyimpan gambar lama jika nama file sama
                        setLogoPreview(`${UPLOAD_URL}${parsed.logo.url}?t=${new Date().getTime()}`);
                    }
                    if (parsed.page?.backgroundImage) {
                        setBgPreview(`${UPLOAD_URL}${parsed.page.backgroundImage}?t=${new Date().getTime()}`);
                    }
                }
            } catch (error) {
                console.error("Gagal load ID Card settings", error);
            }
        };
        fetchSettings();
    }, []);

    // Handlers
    const handleConfigChange = (section, key, value) => {
        setIdCardConfig(prev => ({
            ...prev,
            [section]: { ...prev[section], [key]: value }
        }));
    };

    const handlePageSizeChange = (key, mmValue) => {
        const ptValue = mmToPt(parseFloat(mmValue) || 0);
        handleConfigChange('page', key, ptValue);
    };

    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            const objectUrl = URL.createObjectURL(file);
            if (type === 'logo') {
                setLogoFile(file);
                setLogoPreview(objectUrl);
            } else {
                setBgFile(file);
                setBgPreview(objectUrl);
            }
        }
    };

    const saveIdCardSetting = async () => {
        setIsLoading(true);
        try {
            const formData = new FormData();
            if (logoFile) formData.append('logo_image', logoFile);
            if (bgFile) formData.append('bg_image', bgFile);
            
            formData.append('config_json', JSON.stringify(idCardConfig));

            await api.patch('/settings/3', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success("Template ID Card berhasil disimpan!");
            if(reloadSettings) reloadSettings(); 
        } catch (error) {
            console.error(error);
            toast.error("Gagal menyimpan setting.");
        } finally {
            setIsLoading(false);
        }
    };

    const resetToDefault = () => {
        if(window.confirm("Reset pengaturan tampilan ke default?")) {
            setIdCardConfig(defaultConfig);
            setLogoFile(null);
            setBgFile(null);
            setLogoPreview(null);
            setBgPreview(null);
        }
    };

    return (
        <div className="row g-4 animate-fade-in">
            {/* KOLOM KIRI: FORM CONFIG */}
            <div className="col-lg-7">
                <div className="card border-0 shadow-sm rounded-4">
                    <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                        <h6 className="fw-bold m-0 text-primary"><FaPalette className="me-2"/> Editor Tampilan</h6>
                        <button onClick={resetToDefault} className="btn btn-sm btn-outline-secondary" title="Reset Default"><FaRedo/></button>
                    </div>
                    <div className="card-body p-4" style={{maxHeight: '750px', overflowY: 'auto'}}>
                        
                        {/* 0. UKURAN KERTAS */}
                        <div className="mb-4 border p-3 rounded bg-warning bg-opacity-10 border-warning">
                            <h6 className="fw-bold text-dark small mb-3"><FaRulerCombined className="me-2"/> UKURAN KERTAS (ID CARD)</h6>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Lebar (mm)</label>
                                    <div className="input-group input-group-sm">
                                        <input type="number" className="form-control" 
                                            value={ptToMm(idCardConfig.page.width)} 
                                            onChange={(e) => handlePageSizeChange('width', e.target.value)} 
                                        />
                                        <span className="input-group-text">mm</span>
                                    </div>
                                    <small className="text-muted" style={{fontSize: '10px'}}>Default: 90mm</small>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Tinggi (mm)</label>
                                    <div className="input-group input-group-sm">
                                        <input type="number" className="form-control" 
                                            value={ptToMm(idCardConfig.page.height)} 
                                            onChange={(e) => handlePageSizeChange('height', e.target.value)} 
                                        />
                                        <span className="input-group-text">mm</span>
                                    </div>
                                    <small className="text-muted" style={{fontSize: '10px'}}>Default: 120mm</small>
                                </div>
                            </div>
                        </div>

                        {/* 1. LOGO & HEADER */}
                        <div className="mb-4 border p-3 rounded bg-light">
                            <h6 className="fw-bold text-dark small mb-3">1. LOGO & HEADER</h6>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Warna Header</label>
                                    <div className="input-group input-group-sm">
                                        <input type="color" className="form-control form-control-color" 
                                            value={idCardConfig.header.backgroundColor} 
                                            onChange={(e) => handleConfigChange('header', 'backgroundColor', e.target.value)} 
                                        />
                                        <input type="text" className="form-control" value={idCardConfig.header.backgroundColor} readOnly />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Upload Logo</label>
                                    <input type="file" className="form-control form-control-sm" accept="image/*" onChange={(e) => handleFileChange(e, 'logo')} />
                                    <div className="form-check mt-1">
                                        <input className="form-check-input" type="checkbox" checked={idCardConfig.logo.show} 
                                            onChange={(e) => handleConfigChange('logo', 'show', e.target.checked)} 
                                        />
                                        <label className="form-check-label small">Tampilkan Logo</label>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Tinggi Header (px)</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.header.height} 
                                        onChange={(e) => handleConfigChange('header', 'height', parseInt(e.target.value))} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Ukuran Logo (px)</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.logo.width} 
                                        onChange={(e) => handleConfigChange('logo', 'width', parseInt(e.target.value))} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Posisi Atas Logo</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.logo.marginTop} 
                                        onChange={(e) => handleConfigChange('logo', 'marginTop', parseInt(e.target.value))} 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 2. JUDUL EVENT */}
                        <div className="mb-4 border p-3 rounded bg-light">
                            <h6 className="fw-bold text-dark small mb-3">2. JUDUL EVENT</h6>
                            <div className="row g-3">
                                <div className="col-md-12">
                                    <label className="form-label small fw-bold">Teks Judul</label>
                                    <input type="text" className="form-control form-control-sm fw-bold" 
                                        value={idCardConfig.event_title.text} 
                                        onChange={(e) => handleConfigChange('event_title', 'text', e.target.value)} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Ukuran Font</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.event_title.fontSize} 
                                        onChange={(e) => handleConfigChange('event_title', 'fontSize', parseInt(e.target.value))} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Warna Teks</label>
                                    <input type="color" className="form-control form-control-color w-100" 
                                        value={idCardConfig.event_title.color} 
                                        onChange={(e) => handleConfigChange('event_title', 'color', e.target.value)} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Lebar Area</label>
                                    <input type="text" className="form-control form-control-sm" 
                                        value={idCardConfig.event_title.width} 
                                        onChange={(e) => handleConfigChange('event_title', 'width', e.target.value)} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Margin Atas</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.event_title.marginTop} 
                                        onChange={(e) => handleConfigChange('event_title', 'marginTop', parseInt(e.target.value))} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Alignment</label>
                                    <select className="form-select form-select-sm" 
                                        value={idCardConfig.event_title.textAlign} 
                                        onChange={(e) => handleConfigChange('event_title', 'textAlign', e.target.value)}
                                    >
                                        <option value="left">Kiri</option>
                                        <option value="center">Tengah</option>
                                        <option value="right">Kanan</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* 3. FOTO & IDENTITAS */}
                        <div className="mb-4 border p-3 rounded bg-light">
                            <h6 className="fw-bold text-dark small mb-3">3. FOTO & IDENTITAS</h6>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Margin Atas Foto</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.photo.marginTop} 
                                        onChange={(e) => handleConfigChange('photo', 'marginTop', parseInt(e.target.value))} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Border Foto</label>
                                    <input type="color" className="form-control form-control-color w-100" 
                                        value={idCardConfig.photo.borderColor} 
                                        onChange={(e) => handleConfigChange('photo', 'borderColor', e.target.value)} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Tebal Border</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.photo.borderWidth} 
                                        onChange={(e) => handleConfigChange('photo', 'borderWidth', parseInt(e.target.value))} 
                                    />
                                </div>

                                <div className="col-md-12 border-top my-2"></div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Ukuran Nama Atlet</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.athlete_name.fontSize} 
                                        onChange={(e) => handleConfigChange('athlete_name', 'fontSize', parseInt(e.target.value))} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Warna Nama Atlet</label>
                                    <input type="color" className="form-control form-control-color w-100" 
                                        value={idCardConfig.athlete_name.color} 
                                        onChange={(e) => handleConfigChange('athlete_name', 'color', e.target.value)} 
                                    />
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Ukuran Nama Tim</label>
                                    <input type="number" className="form-control form-control-sm" 
                                        value={idCardConfig.team_name.fontSize} 
                                        onChange={(e) => handleConfigChange('team_name', 'fontSize', parseInt(e.target.value))} 
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-bold">Warna Nama Tim</label>
                                    <input type="color" className="form-control form-control-color w-100" 
                                        value={idCardConfig.team_name.color} 
                                        onChange={(e) => handleConfigChange('team_name', 'color', e.target.value)} 
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 4. BADGES & FOOTER */}
                        <div className="mb-4 border p-3 rounded bg-light">
                            <h6 className="fw-bold text-dark small mb-3">4. LAIN-LAIN</h6>
                            <div className="row g-3">
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">BG Kyorugi</label>
                                    <input type="color" className="form-control form-control-color w-100" 
                                        value={idCardConfig.badges.kyorugiBg} 
                                        onChange={(e) => handleConfigChange('badges', 'kyorugiBg', e.target.value)} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">BG Poomsae</label>
                                    <input type="color" className="form-control form-control-color w-100" 
                                        value={idCardConfig.badges.poomsaeBg} 
                                        onChange={(e) => handleConfigChange('badges', 'poomsaeBg', e.target.value)} 
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label small fw-bold">Teks Footer</label>
                                    <input type="color" className="form-control form-control-color w-100" 
                                        value={idCardConfig.footer.mainTextColor} 
                                        onChange={(e) => handleConfigChange('footer', 'mainTextColor', e.target.value)} 
                                    />
                                </div>
                                <div className="col-md-12">
                                    <label className="form-label small fw-bold">Upload Background</label>
                                    <input type="file" className="form-control form-control-sm" accept="image/*" onChange={(e) => handleFileChange(e, 'bg')} />
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 text-end">
                            <button onClick={saveIdCardSetting} className="btn btn-primary btn-lg rounded-pill px-5 shadow-sm" disabled={isLoading}>
                                {isLoading ? 'Menyimpan...' : <><FaSave className="me-2"/> Simpan Template</>}
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* KOLOM KANAN: LIVE PREVIEW */}
            <div className="col-lg-5">
                <div className="sticky-top" style={{ top: '20px' }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="fw-bold text-dark m-0"><FaIdCard className="me-2"/> Live Preview</h6>
                        <span className="badge bg-secondary">
                            {ptToMm(idCardConfig.page.width)}mm x {ptToMm(idCardConfig.page.height)}mm
                        </span>
                    </div>
                    
                    <div className="d-flex justify-content-center bg-dark bg-opacity-10 rounded-4 p-5 shadow-inner" style={{minHeight: '500px'}}>
                        
                        {/* PREVIEW CONTAINER */}
                        <div className="position-relative bg-white shadow-lg overflow-hidden d-flex flex-column"
                            style={{
                                width: `${idCardConfig.page.width}px`,
                                height: `${idCardConfig.page.height}px`,
                                backgroundColor: idCardConfig.page.backgroundColor,
                                transform: 'scale(1.0)',
                                transformOrigin: 'top center',
                                fontFamily: 'Arial, sans-serif'
                            }}
                        >
                            {/* BG IMAGE */}
                            {bgPreview && (
                                <img 
                                    src={bgPreview} 
                                    alt="bg" 
                                    className="position-absolute w-100 h-100 object-fit-cover" 
                                    style={{zIndex: 0, opacity: 1}} 
                                    onError={(e) => e.target.style.display = 'none'} // Sembunyikan jika error
                                />
                            )}

                            {/* HEADER */}
                            <div style={{
                                height: `${idCardConfig.header.height}px`,
                                backgroundColor: idCardConfig.header.backgroundColor,
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: idCardConfig.header.alignItems,
                                justifyContent: 'space-between',
                                paddingTop: `${idCardConfig.header.paddingTop}px`,
                                paddingLeft: '12px', paddingRight: '12px',
                                zIndex: 1
                            }}>
                                {/* LOGO */}
                                {idCardConfig.logo.show && logoPreview ? (
                                    <img 
                                        src={logoPreview} 
                                        alt="Logo" 
                                        style={{
                                            width: `${idCardConfig.logo.width}px`,
                                            height: `${idCardConfig.logo.width}px`, 
                                            marginTop: `${idCardConfig.logo.marginTop}px`,
                                            objectFit: 'contain'
                                        }} 
                                        onError={(e) => {
                                            e.target.style.display = 'none'; // Sembunyikan jika error
                                            console.log("Logo preview load failed:", logoPreview);
                                        }}
                                    />
                                ) : <div style={{width: 40}}></div>}

                                {/* EVENT TITLE */}
                                <div style={{ width: idCardConfig.event_title.width, textAlign: idCardConfig.event_title.textAlign }}>
                                    <div style={{
                                        color: idCardConfig.event_title.color,
                                        fontSize: `${idCardConfig.event_title.fontSize + 4}px`,
                                        fontWeight: idCardConfig.event_title.fontWeight,
                                        marginTop: `${idCardConfig.event_title.marginTop}px`,
                                        lineHeight: 1.1
                                    }}>
                                        {idCardConfig.event_title.text}
                                    </div>
                                </div>
                            </div>

                            {/* PHOTO AREA */}
                            <div className="d-flex justify-content-center position-relative" style={{ zIndex: 2, marginTop: `${idCardConfig.photo.marginTop}px` }}>
                                <div style={{
                                    width: `${idCardConfig.photo.width}px`,
                                    height: `${idCardConfig.photo.height}px`,
                                    backgroundColor: idCardConfig.photo.backgroundColor,
                                    border: `${idCardConfig.photo.borderWidth}px solid ${idCardConfig.photo.borderColor}`,
                                    borderRadius: `${idCardConfig.photo.borderRadius}px`,
                                    backgroundImage: 'url(https://as2.ftcdn.net/v2/jpg/02/17/34/67/1000_F_217346782_7XpCTt8bLNJqvVAaDZJwvZjm0epQmj6j.jpg)',
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center'
                                }}></div>
                            </div>

                            {/* BODY TEXT */}
                            <div className="flex-grow-1 px-3 text-center position-relative d-flex flex-column justify-content-start pt-3" style={{ zIndex: 1 }}>
                                <div style={{
                                    color: idCardConfig.athlete_name.color,
                                    fontSize: `${idCardConfig.athlete_name.fontSize + 2}px`,
                                    fontWeight: idCardConfig.athlete_name.fontWeight,
                                    textTransform: 'uppercase',
                                    marginTop: `${idCardConfig.athlete_name.marginTop}px`,
                                    marginBottom: `${idCardConfig.athlete_name.marginBottom}px`
                                }}>
                                    Nama Atlet
                                </div>

                                <div style={{
                                    color: idCardConfig.team_name.color,
                                    fontSize: `${idCardConfig.team_name.fontSize + 1}px`,
                                    fontWeight: idCardConfig.team_name.fontWeight,
                                    textTransform: 'uppercase',
                                    marginBottom: `${idCardConfig.team_name.marginBottom}px`
                                }}>
                                    KONTINGEN GARUDA
                                </div>
                                
                                <div className="d-flex justify-content-center gap-2 mb-2 align-items-center">
                                    <span style={{
                                        backgroundColor: idCardConfig.badges.kyorugiBg,
                                        color: idCardConfig.badges.textColor,
                                        fontSize: `${idCardConfig.badges.fontSize + 1}px`,
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase'
                                    }}>
                                        KYORUGI PRESTASI
                                    </span>
                                    <span style={{
                                        fontSize: `${idCardConfig.badges.fontSize + 2}px`,
                                        fontWeight: 'bold',
                                        color: '#334155'
                                    }}>
                                        JUNIOR
                                    </span>
                                </div>

                                <div style={{
                                    color: idCardConfig.detail_text.color,
                                    fontSize: `${idCardConfig.detail_text.fontSize + 1}px`,
                                    fontWeight: 'bold',
                                    borderTop: `1px dashed ${idCardConfig.detail_text.borderColor}`,
                                    paddingTop: '6px',
                                    marginTop: `${idCardConfig.detail_text.marginTop}px`,
                                    width: '80%',
                                    alignSelf: 'center'
                                }}>
                                    Under 55 kg
                                </div>
                            </div>
                        </div>
                    </div>
                    <p className="text-center text-muted small mt-3">*Preview ini adalah simulasi HTML. PDF asli akan mengikuti dimensi yang tepat.</p>
                </div>
            </div>
        </div>
    );
};

export default TabIdCard;